import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import styles from './ShowMeet.module.css';
import routes from 'routes';

import { getMeetComplete } from 'api/meetApi';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';

import { generateMeetDetail } from 'services/admin/MeetService';
import {
  MEET_TYPE,
  clubFeaturesOf,
  clubForId,
  meetTypeName,
} from 'utils/clubFeatures';
import { convertDateTime } from 'utils/time';

import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import MetaTags from 'components/common/MetaTags';

import competitionMeetImage from 'assets/images/uicc.png';
import trainingMeetImage from 'assets/images/uifd.png';
import { ReactComponent as CompetitionIcon } from 'assets/images/icon_competition.svg';
import { ReactComponent as TrainingIcon } from 'assets/images/icon_training.svg';
import SeasonPointsHelp from 'components/admin/SeasonPointsHelp';
import MeetPageHeader from 'components/NewDesign/MeetPageHeader';
import VolunteerSummary from 'components/admin/volunteer/VolunteerSummary';
import ProgramViewer from 'components/admin/ProgramViewer/ProgramViewer';
import MeetStatsChart from 'components/common/MeetStatsChart';
import ResultSyncFailureBanner from 'components/admin/ResultSyncFailureBanner';
import { getMeetAthletesStats } from 'api/userApi';
import { getMeetRegistrationStatsByMeetId } from 'api/meetRegistrationApi';
import CustomNewButton from 'components/NewDesign/CustomNewButton';

const ShowMeet = () => {
  const userPermissionLevel = Number(getUserPermissionLevel());
  const navigate = useNavigate();

  const { clubId, meetId } = useParams();
  const [meet, setMeet] = useState({});
  const [program, setProgram] = useState({});
  const [fetchingMeet, setFetchingMeet] = useState(true);
  const [meetCompleteData, setMeetCompleteData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [meetType, setMeetType] = useState('single-club');

  // Parse ageEventMapping when meet data is loaded
  // useEffect(() => {
  //   if (meet.ageEventMapping) {
  //     const parsedData = parseAgeEventMapping(meet.ageEventMapping);
  //     setAgeGroupEventData(parsedData);

  //     // Get all unique events across all age groups
  //     const allEventIds = new Set();
  //     Object.values(parsedData).forEach((events) => {
  //       events.forEach((eventId) => allEventIds.add(eventId));
  //     });

  //     // Convert event IDs to labels
  //     const eventLabels = getEventTypeLabel(
  //       Array.from(allEventIds).join(','),
  //       true
  //     );
  //     setAllEvents(eventLabels);
  //   }
  // }, [meet.ageEventMapping]);

  // Parse ageEventMapping string into structured data
  // const parseAgeEventMapping = (ageEventMapping) => {
  //   const result = {};

  //   if (!ageEventMapping) return result;

  //   try {
  //     const ageGroupPairs = ageEventMapping.split('|');

  //     ageGroupPairs.forEach((pair) => {
  //       const [ageGroupId, eventIds] = pair.split(':');
  //       if (ageGroupId && eventIds) {
  //         result[ageGroupId] = eventIds.split(',').map((id) => id.trim());
  //       }
  //     });
  //   } catch (err) {
  //     console.error('Error parsing ageEventMapping:', err);
  //   }

  //   return result;
  // };

  // // Get available age groups from the meet data
  // const getAvailableAgeGroups = () => {
  //   const ageGroupIds = Object.keys(ageGroupEventData);
  //   return ageGroupOptions
  //     .filter((option) => ageGroupIds.includes(option.value))
  //     .map((option) => ({
  //       value: option.value,
  //       label: option.label,
  //     }));
  // };

  // // Get events for display based on selected age groups
  // const getDisplayEvents = () => {
  //   if (selectedAgeGroups.length === 0) {
  //     // Show all events if no age group is selected
  //     return allEvents;
  //   }

  //   // Show events for selected age groups
  //   const selectedEventIds = new Set();
  //   selectedAgeGroups.forEach((ageGroupId) => {
  //     if (ageGroupEventData[ageGroupId]) {
  //       ageGroupEventData[ageGroupId].forEach((eventId) => {
  //         selectedEventIds.add(eventId);
  //       });
  //     }
  //   });

  //   // Convert to labels
  //   return getEventTypeLabel(Array.from(selectedEventIds).join(','), true);
  // };

  // // Check if an event is a field event
  // const getIsFieldEvent = (eventLabel) => {
  //   return fieldEvents.includes(eventLabel) || eventLabel === 'High Jump';
  // };

  // const handleAgeGroupChange = (ageGroupId) => {
  //   setSelectedAgeGroups((prev) => {
  //     if (prev.includes(ageGroupId)) {
  //       // If clicking the already selected item, deselect it (show all events)
  //       return [];
  //     } else {
  //       // Select only this one age group
  //       return [ageGroupId];
  //     }
  //   });
  // };

  const fetchMeet = useCallback(async () => {
    try {
      // Use the new complete API
      const response = await getMeetComplete(clubId, meetId);

      // [DOUG] We will need to use program when the Event PRogram viewer is added
      //const { meet: meetData, program, volunteerRequirements } = response.data;

      // Store complete data for child components
      setMeetCompleteData(response.data);

      // Process meet data for display
      const { meet: meetData, program } = response.data;
      setMeet(generateMeetDetail(meetData, true));
      setProgram(program);

      // Get meet registration stats
      let statsResponse = null;
      if (meetData.meetType === 2) {
        // if the meet type is multi club
        setMeetType('multi-club');
        statsResponse = await getMeetRegistrationStatsByMeetId(meetId);
      } else {
        setMeetType('single-club');
        statsResponse = await getMeetAthletesStats(meetId);
      }
      setStatsData(statsResponse?.data);
    } catch (err) {
      console.log('[ShowMeet] Fetch result error: ', err);
      if (err.response?.status === 404) {
        navigate(routes.notFound);
      } else if (err.response?.status === 403) {
        // Handle 403 Forbidden - user doesn't have permission
        toast.error(
          err.response?.data?.message ||
            "You don't have permission to view this meet"
        );
        navigate(routes.admin.dashboard); // or wherever you want to redirect them
      } else {
        toast.error(
          err.response?.data?.message || 'Failed to load meet details'
        );
        navigate(routes.admin.dashboard);
      }
    }
  }, [clubId, meetId, navigate]);

  useEffect(() => {
    fetchMeet().then(() => {
      setFetchingMeet(false);
    });
  }, [fetchMeet]);

  // Same formatter the club dashboard uses for its "date • time" line, so the
  // header reads identically to the meet card the user just clicked through.
  const { date: meetDateLabel, time: meetTimeLabel } = convertDateTime(
    meet.meetDate
  );

  // What this meet's club calls the meet type (a school reads Competition). Worked out here, from the club list
  // already in memory, and not when the meet is fetched, so the club list reloading never refetches the meet.
  const club = useSelector((state) => clubForId(state.user, clubId));
  const meetTypeLabel = meetTypeName(club, meet.meetType, 'detail');

  const meetDetailConfig = [
    { key: 'meetTypeLabel', label: 'Type', render: () => meetTypeLabel },
    { key: 'meetStatus', label: 'Status' },
    { key: 'location', label: 'Location' },
    {
      key: 'seasonPointsLabel',
      label: 'Season points',
      render: (meetData) => (
        <SeasonPointsHelp
          countsForSeasonPoints={meetData.countsForSeasonPoints}
          countsForSeasonBests={meetData.countsForSeasonBests}
          pointsExplanation={meetData.pointsExplanation}
          hasHouses={clubFeaturesOf(club).teams}
        />
      ),
    },
  ];

  return (
    <>
      <MetaTags title="SplitFast | Meet Detail" />
      <AdminTablePageLayout loading={fetchingMeet}>
        <MeetPageHeader
          title="Meet Details"
          meetName={meet.meetName}
          meetDate={meetDateLabel}
          meetTime={meetTimeLabel}
        >
          {userPermissionLevel >= UserPermissionLevel.Admin && (
            <CustomNewButton
              onClick={() => navigate(routes.admin.editMeet.url(clubId, meetId))}
              label="Edit Meet"
            ></CustomNewButton>
          )}
          {/* One entry point. Officials+ manage the full roster (admin view); everyone else self-serves their own
              athletes ("My Registrations"). The self-service page also handles the self-service-OFF case (shows the
              participating clubs + a "managed by the club" message), so non-admins can always land there. */}
          {meetType === 'multi-club' && (
            <CustomNewButton
              onClick={() =>
                navigate(
                  userPermissionLevel >= UserPermissionLevel.Official
                    ? routes.admin.meetGeneration.url(clubId, meetId)
                    : routes.admin.myMeetRegistrations.url(clubId, meetId)
                )
              }
              label="Registrations"
              variant="primary2"
            ></CustomNewButton>
          )}
          {userPermissionLevel >= UserPermissionLevel.Guest && (
            <CustomNewButton
              onClick={() =>
                navigate(routes.admin.meetStartLists.url(clubId, meetId))
              }
              label="Start Lists"
              variant="primary2"
            ></CustomNewButton>
          )}
          <CustomNewButton
            onClick={() => navigate(routes.admin.events.url(clubId, meetId))}
            label="Results"
            variant="success"
          ></CustomNewButton>
          <CustomNewButton
            onClick={() =>
              navigate(routes.admin.trainingResults.url(clubId, meetId))
            }
            label="Training"
            variant="primary2"
          ></CustomNewButton>
          {/* Admins on every meet. A school's members on its competitions too (points slice 4b): the
              page tells them when the points have not been released yet. A training session has no
              points. */}
          {(userPermissionLevel >= UserPermissionLevel.Admin ||
            (clubFeaturesOf(club).teams &&
              meet.meetType !== MEET_TYPE.TRAINING)) && (
            <CustomNewButton
              onClick={() =>
                navigate(routes.admin.meetPointsDashboard.url(clubId, meetId))
              }
              label="Points"
              variant="orange"
            ></CustomNewButton>
          )}
        </MeetPageHeader>

        {/* Main Meet Info Card */}
        <div className={styles.meetDetailsCard}>
          <div className={styles.meetDetailsCardLeft}>
            <div className={styles.imageDescriptionContainer}>
              <div className={styles.imageContainer}>
                <div className={styles.image}>
                  <img
                    src={
                      meet.meetType === 3
                        ? trainingMeetImage
                        : competitionMeetImage
                    }
                    alt={meet.meetName}
                  />
                </div>
                <div
                  className={`${styles.tagWrapper} ${meet.meetType === 3 ? styles.trainingTagWrapper : ''}`}
                >
                  <div className={styles.tagImage}>
                    {meet.meetType === 3 ? (
                      <TrainingIcon />
                    ) : (
                      <CompetitionIcon />
                    )}
                  </div>
                  <div className={styles.tagText}>{meetTypeLabel}</div>
                </div>
                <div className={styles.imageCaption}>{meet.description}</div>
              </div>
            </div>

            <div className={styles.details}>
              {meetDetailConfig.map((detail, index) => (
                <div className={styles.detail} key={index}>
                  <div className={styles.detailTitle}>{detail.label}</div>
                  <div className={styles.detailValue}>
                    {detail.render ? detail.render(meet) : meet[detail.key]}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Meet Statistics Chart */}
          <MeetStatsChart
            statsData={statsData}
            loading={fetchingMeet}
            meetType={meetType}
            meetStatus={meet.meetStatus}
          />
        </div>

        {/* Results sync failure banner — admins only, renders nothing unless
            failed sync jobs exist (detail and retry live in the Results Sync
            report). The banner fetches sync-status on mount, and members view
            this page heavily, so the permission gate here is what keeps the
            admin-only API from being called on every member page view. */}
        {userPermissionLevel >= UserPermissionLevel.Admin && (
          <ResultSyncFailureBanner meetId={meetId} />
        )}

        {/* Age Groups and Events Section */}
        {program && <ProgramViewer programData={program}></ProgramViewer>}

        {/* Updated VolunteerSummary with passed data */}
        {meetCompleteData && (
          <VolunteerSummary
            meetId={meetId}
            clubId={clubId}
            userPermissionLevel={userPermissionLevel}
            volunteerRequirements={meetCompleteData.volunteerRequirements}
            onRequirementsUpdate={fetchMeet} // Refresh callback
          />
        )}
      </AdminTablePageLayout>
    </>
  );
};

export default ShowMeet;
